import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { LogoIcon } from "@/components/Logo";

const PAID_PLANS = ["starter", "pro", "scale"];

export default function Login() {
  const { user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const planParam = (searchParams.get("plan") || "").trim().toLowerCase();
  const afterAuthPath = PAID_PLANS.includes(planParam)
    ? `/app/plan-checkout?plan=${encodeURIComponent(planParam)}`
    : "/app/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  if (user) return <Navigate to={afterAuthPath} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(afterAuthPath);
    else setErr(res.error || "Falha no login");
  };

  return (
    <div className="min-h-screen bg-cp-bg text-slate-100 font-body flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-white/[0.06]">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1771931059161-d33d71ae2780?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-cp-bg via-cp-bg/80 to-cp-bg" />
        <div className="relative p-16 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo-link">
            <LogoIcon size={40} />
            <span className="font-heading font-bold text-xl tracking-tighter">ConvertPix</span>
          </Link>
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-4">_recovery engine</div>
            <h2 className="font-heading text-4xl font-semibold tracking-tight leading-tight">
              Todo dia PIX não-pago é dinheiro que some do seu caixa.
            </h2>
            <p className="mt-4 text-slate-400 max-w-md">
              Conecte em 5 minutos, veja a recuperação acontecer em segundos.
            </p>
          </div>
          <div className="text-xs text-slate-600 font-mono">recuperação automática · 24/7</div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10" data-testid="mobile-logo-link">
            <LogoIcon size={36} />
            <span className="font-heading font-bold text-lg">ConvertPix</span>
          </Link>

          <div className="mb-10">
            <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-3">_entrar</div>
            <h1 className="font-heading text-3xl font-bold tracking-tighter">Bem-vindo de volta</h1>
            <p className="text-slate-400 text-sm mt-2">Entre na sua conta pra continuar recuperando vendas.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-lg transition-all"
                placeholder="voce@empresa.com"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Senha</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary focus:ring-2 focus:ring-cp-primary/30 outline-none text-sm px-4 py-3 rounded-lg transition-all"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-cp-primary hover:bg-cp-primary-hover disabled:opacity-60 text-cp-bg font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {loading ? "Entrando..." : <>Entrar <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="text-sm text-slate-400 mt-8 text-center">
            Ainda não tem conta?{" "}
            <Link
              to={PAID_PLANS.includes(planParam) ? `/signup?plan=${encodeURIComponent(planParam)}` : "/signup"}
              data-testid="login-to-signup"
              className="text-cp-primary-light hover:text-cp-primary-light font-semibold"
            >
              Criar conta grátis
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
